import { DataFieldUsage } from 'src/app/services/api.services';
import { DataAggregation } from '../dataModel';
import { VisualFieldNotAllowedReason } from './CanAddVisualFieldError';
import { Visual } from './Visual';
import { VisualFieldTarget } from './VisualFieldTarget';

let nextRoleId = 1;

export class VisualFieldRoleType<TVisual extends Visual = Visual<any, any, any>> {
  readonly id = nextRoleId++;

  constructor(
    public readonly name: string,
    private readonly properties: VisualFieldRoleType.Properties) { }

  readonly kind = this.properties.kind;

  readonly title = this.properties.title;
  readonly icon = this.properties.icon;
  readonly dropText = this.properties.dropText ?? `Drop ${this.title} Here`;
  readonly description = this.properties.description ?? this.title;
  readonly displayOrder = this.properties.displayOrder ?? (this.kind as number);
  readonly itemStyle = this.properties.itemStyle ?? {};
  readonly maxDisplayedItems = this.properties.maxDisplayedItems ?? null;

  readonly usagePriority = this.properties.usagePriority;
  readonly allowsValues = this.properties.allowsValues;
  readonly allowsMeasures = this.properties.allowsMeasures;
  readonly allowsDuplicates = this.properties.allowsDuplicates;

  readonly limits =
    unwrapCallback<(v: TVisual) => VisualFieldRoleType.Limits>(this.properties.limits ?? { min: 0 });

  readonly isRequired =
    unwrapCallback<(v: TVisual) => boolean>(this.properties.isRequired ?? false);
    
  readonly isHidden =
    unwrapCallback<(v: TVisual) => boolean>(this.properties.isHidden ?? false);

  readonly isEnabled =
    unwrapCallback<(v: TVisual) => boolean>(this.properties.isEnabled ?? true);

  readonly fieldAllowed =
    unwrapCallback<(v: TVisual, f: VisualFieldTarget) => VisualFieldNotAllowedReason>(this.properties.fieldAllowed);

  readonly sortable =
    unwrapCallback<(v: TVisual) => boolean | number>(this.properties.sortable ?? true);

  readonly aggregationAllowed =
    unwrapCallback<(v: TVisual, f: VisualFieldTarget, aggr: DataAggregation) => boolean>(this.properties.aggregationAllowed ?? true);
}

function unwrapCallback<T extends ((...args: any[]) => any)>(callbackOrValue: T | ReturnType<T>): T {
  return (typeof callbackOrValue === 'function')
    ? <any>callbackOrValue
    : (() => callbackOrValue);
}


export type VisualFieldRoleTypeOrKind = VisualFieldRoleType | VisualFieldRoleType.Kind;

export namespace VisualFieldRoleType {
  export enum Kind {
    /** Role for fields that aren't included in the visual itself, e.g. filter targets. */
    None,

    /** Role for fields that define the primary grouping. */
    Grouping,

    /** Role for additional details to display for a distinct group, e.g. legend. */
    Detail,

    /** Role for fields that are the values of interest in a visual, per distinct group. */
    Values,

    /** Role for fields included in tooltips. */
    Tooltip,
  }

  export type MinMax = Readonly<{ min: number; max?: number; }>;

  export type Limits = Readonly<(
    MinMax |
    { groupings?: MinMax, measures: MinMax; } |
    { groupings: MinMax, measures?: MinMax; }
  )>;

  export interface Properties<TVisual extends Visual = Visual<any, any, any>> {
    readonly kind: Kind;

    readonly title: string;
    readonly icon: string;
    readonly dropText?: string;
    readonly description?: string;
    readonly displayOrder?: number;
    readonly itemStyle?: { background?: string; textColor?: string; };
    readonly maxDisplayedItems?: number;

    readonly usagePriority: number;
    readonly allowsValues: boolean;
    readonly allowsMeasures: boolean;
    readonly allowsDuplicates?: boolean;
    readonly limits: Limits | ((visual: TVisual) => Limits);

    /**
     * Callback that is used to check if the role is required for the given visual. */
    readonly isRequired?: boolean | ((visual: TVisual) => boolean);
    
    /**
     * Callback that is used to check if the role is hidden for the given visual.
     * This is fired whenever visual options change.
     * This is not fired when visual state changes. */
    readonly isHidden?: (visual: TVisual) => boolean;

    /**
     * Callback that is used to check if the role is enabled for the given visual.
     * This is fired whenever visual options change.
     * This is not fired when visual state changes. */
    readonly isEnabled?: (visual: TVisual) => boolean;

    /**
     * Callback used to check if a field can be added to this role of the given visual.
     * This is called after @see allowsValues, @see allowsMeasures and @see limits checks are done. */
    readonly fieldAllowed?: (visual: TVisual, field: VisualFieldTarget) => VisualFieldNotAllowedReason | null;

    /**
     * Callback to check if the visual can apply sorting to fields in this role. 
     * Should return a boolean or a number. The former defines the maximum number of sorting fields. */
    readonly sortable?: boolean | ((visual: TVisual) => boolean | number);

    /**
     * Callback to check if the specified aggregation can be applied to a field.
     **/
    readonly aggregationAllowed?: (visual: TVisual, field: VisualFieldTarget, aggr: DataAggregation) => boolean;
  }

  export namespace Defaults {
    // usagePriority defaults to:
    // Values   - highest
    // Grouping - 2nd
    // Detail   - 3rd
    // Tooltip  - 4th

    export const defaultValueItemStyle = {
      background: '#f29100',
      textColor: 'white',
    };

    export const defaultMeasureItemStyle = {
      background: '#004c77',
      textColor: 'white',
    };


    export const fields: Properties = {
      kind: VisualFieldRoleType.Kind.Grouping,
      title: 'Fields',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Field Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 22,
      displayOrder: 998,

      usagePriority: 998,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 0 },
      sortable: true,
    };

    export const measures: Properties = {
      kind: VisualFieldRoleType.Kind.Values,
      title: 'Measures',
      icon: 'fas fa-ruler-combined',
      dropText: 'Drop Measure Here',
      description: '',
      itemStyle: defaultMeasureItemStyle,
      maxDisplayedItems: 12,
      displayOrder: 999,

      usagePriority: 999,
      allowsValues: false,
      allowsMeasures: true,
      limits: { min: 1 },
      sortable: true,
    };


    export const tableFields: Properties = {
      kind: VisualFieldRoleType.Kind.Values,
      title: 'Fields',
      icon: 'fas fa-ruler-combined',
      dropText: 'Drop Field Here',
      description: '',
      itemStyle: defaultMeasureItemStyle,
      maxDisplayedItems: 22,

      usagePriority: 999,
      allowsValues: true,
      allowsMeasures: true,
      limits: { min: 1 },
      sortable: true,
      aggregationAllowed: (v, f, a) => {
        if (!a) {
          if (f.dataType.isNumeric && f.schemaItem?.getDefaultUsage() === DataFieldUsage.Measure) {
            return false;
          }  
        }

        return true;
      }
    };


    export const pivotTableRows: Properties = {
      kind: VisualFieldRoleType.Kind.Grouping,
      title: 'Rows',
      icon: 'fad fa-line-columns',
      dropText: 'Drop Row Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 6,

      usagePriority: 300,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 0 },
      sortable: true,
    };

    export const pivotTableColumns: Properties = {
      kind: VisualFieldRoleType.Kind.Detail,
      title: 'Columns',
      icon: 'fas fa-columns',
      dropText: 'Drop Column Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 3,

      usagePriority: 200,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 0 },
      sortable: true,
    };

    export const pivotTableMeasures: Properties = {
      kind: VisualFieldRoleType.Kind.Values,
      title: 'Measures',
      icon: 'fas fa-ruler-combined',
      dropText: 'Drop Measure Here',
      description: '',
      itemStyle: defaultMeasureItemStyle,
      maxDisplayedItems: 6,

      usagePriority: 999,
      allowsValues: false,
      allowsMeasures: true,
      limits: { min: 0 },
      sortable: true,
    };


    export const groupingAxis: Properties = {
      kind: VisualFieldRoleType.Kind.Grouping,
      title: 'Axis',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Axis Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 1,

      usagePriority: 300,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 1, max: 1 },
      sortable: true,
    };

    export const groupingLegend: Properties = {
      kind: VisualFieldRoleType.Kind.Grouping,
      title: 'Legend',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Legend Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 1,

      usagePriority: 300,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 1, max: 1 },
      sortable: false,
    };

    export const groupingGroup: Properties = {
      kind: VisualFieldRoleType.Kind.Grouping,
      title: 'Group',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Group Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 1,

      usagePriority: 300,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 1, max: 1 },
      sortable: true,
    };

    export const groupingCategory: Properties = {
      kind: VisualFieldRoleType.Kind.Grouping,
      title: 'Category',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Category Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 1,

      usagePriority: 300,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 1, max: 1 },
      sortable: true,
    };


    export const detailLegend: Properties = {
      kind: VisualFieldRoleType.Kind.Detail,
      title: 'Legend',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Legend Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 1,

      usagePriority: 200,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 0 },
      sortable: false,
    };

    export const detailDetails: Properties = {
      kind: VisualFieldRoleType.Kind.Detail,
      title: 'Details',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Detail Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 1,

      usagePriority: 200,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 0 },
    };

    export const detailBreakdown: Properties = {
      kind: VisualFieldRoleType.Kind.Detail,
      title: 'Breakdown',
      icon: 'fas fa-layer-group',
      dropText: 'Drop Breakdown Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 1,

      usagePriority: 200,
      allowsValues: true,
      allowsMeasures: false,
      limits: { min: 0 },
    };


    export const tooltip: Properties = {
      kind: VisualFieldRoleType.Kind.Tooltip,
      title: 'Tooltip',
      icon: 'far fa-question-circle',
      dropText: 'Drop Tooltip Here',
      description: '',
      itemStyle: defaultValueItemStyle,
      maxDisplayedItems: 2,

      usagePriority: 100,
      allowsValues: true,
      allowsMeasures: true,
      limits: { min: 0 },
    };
  }
}
